import { useEffect, useState } from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { api } from "@/lib/api";

// Curated corridors we service most often. 2BR is a sensible "average household" size
// for a pre-qualification estimate.
const ROUTES = [
  { origin: "Louisville", origin_state: "KY", dest: "Cincinnati", dest_state: "OH", tag: "Most Popular" },
  { origin: "Louisville", origin_state: "KY", dest: "Nashville", dest_state: "TN", tag: null },
  { origin: "Louisville", origin_state: "KY", dest: "Indianapolis", dest_state: "IN", tag: null },
  { origin: "Lexington", origin_state: "KY", dest: "Louisville", dest_state: "KY", tag: "Same-Day" },
  { origin: "Louisville", origin_state: "KY", dest: "Charleston", dest_state: "WV", tag: null },
  { origin: "Louisville", origin_state: "KY", dest: "Columbus", dest_state: "OH", tag: null },
];

const HOME_SIZE = "2br";

async function fetchRouteQuote(r) {
  try {
    const dist = await api.post("/distance", {
      origin_city: r.origin,
      origin_state: r.origin_state,
      destination_city: r.dest,
      destination_state: r.dest_state,
    });
    const miles = dist.data?.miles;
    if (miles == null) return { miles: null, low: null, high: null };
    const quote = await api.post("/quote", {
      home_size: HOME_SIZE,
      move_type: "long_distance",
      distance_miles: miles,
      extra_services: [],
    });
    return {
      miles: Math.round(miles),
      low: quote.data?.low ?? null,
      high: quote.data?.high ?? null,
    };
  } catch (_) {
    return { miles: null, low: null, high: null };
  }
}

function prefillHash(r) {
  // Deep-link to Quote Calculator with pre-filled cities (component reads these via URL)
  const params = new URLSearchParams({
    origin_city: r.origin,
    origin_state: r.origin_state,
    destination_city: r.dest,
    destination_state: r.dest_state,
    home_size: HOME_SIZE,
    move_type: "long_distance",
  });
  return `#quote?${params.toString()}`;
}

export default function PopularRoutes() {
  const [data, setData] = useState(() => ROUTES.map((r) => ({ ...r, miles: null, low: null, high: null, loading: true })));

  useEffect(() => {
    let alive = true;
    (async () => {
      const results = await Promise.all(ROUTES.map(fetchRouteQuote));
      if (!alive) return;
      setData(
        ROUTES.map((r, i) => ({
          ...r,
          ...results[i],
          loading: false,
        }))
      );
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      id="popular-routes"
      className="bg-[#151816] text-white py-20 lg:py-28 border-t border-[#1f1f1f]"
      data-testid="popular-routes-section"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <div className="overline text-[#5BB239]">Popular Routes</div>
            <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
              Priced ahead of time.
              <br />
              <span className="text-[#5BB239]">No guessing.</span>
            </h2>
          </div>
          <p className="max-w-md text-sm text-white/60 leading-relaxed">
            Live estimates for a typical 2-bedroom, long-distance move on our
            most-requested corridors. Final quote is written and pinned before
            we lift a box.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {data.map((r, i) => (
            <a
              key={`${r.origin}-${r.dest}`}
              href={prefillHash(r)}
              className="bg-[#151816] hover:bg-[#1e2320] transition-colors p-6 flex flex-col gap-4 group"
              data-testid={`popular-route-${i}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 text-[#5BB239]">
                  <MapPin className="w-3.5 h-3.5" strokeWidth={2.5} />
                  <span className="font-mono-label text-[9px] tracking-widest">
                    Route {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                {r.tag && (
                  <span className="font-mono-label text-[9px] bg-[#5BB239] text-black px-2 py-0.5 tracking-widest">
                    {r.tag}
                  </span>
                )}
              </div>

              <div className="font-display font-black text-2xl lg:text-[28px] leading-[0.95] tracking-tight">
                {r.origin}, {r.origin_state}
                <div className="my-1.5 text-[#5BB239]">
                  <ArrowRight className="w-5 h-5 inline-block" />
                </div>
                {r.dest}, {r.dest_state}
              </div>

              <div className="mt-auto pt-4 border-t border-white/10 flex items-end justify-between">
                <div>
                  <div className="font-mono-label text-[9px] text-white/45 tracking-widest">
                    2BR · Long-Distance
                  </div>
                  <div className="mt-1 font-display font-black text-xl text-white">
                    {r.loading ? (
                      <span className="inline-block h-6 w-28 bg-white/10 animate-pulse" />
                    ) : r.low != null && r.high != null ? (
                      <>
                        ${r.low.toLocaleString()}
                        <span className="text-white/40 mx-1">–</span>${r.high.toLocaleString()}
                      </>
                    ) : (
                      <span className="text-white/50 text-sm">Call for quote</span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono-label text-[9px] text-white/45 tracking-widest">
                    Distance
                  </div>
                  <div className="mt-1 font-display font-black text-xl text-white">
                    {r.loading ? (
                      <span className="inline-block h-6 w-14 bg-white/10 animate-pulse" />
                    ) : r.miles != null ? (
                      <>{r.miles} <span className="text-white/40 text-xs">mi</span></>
                    ) : (
                      <span className="text-white/50 text-sm">—</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="pt-2 flex items-center gap-2 text-[#5BB239] font-mono-label text-[10px] tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                Get exact quote <ArrowRight className="w-3 h-3" />
              </div>
            </a>
          ))}
        </div>

        <p className="mt-8 text-center text-[11px] font-mono-label text-white/40 tracking-widest">
          Estimates use our published rates · Locked in writing before booking · No hidden fees
        </p>
      </div>
    </section>
  );
}
